import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { debounceTime, map } from 'rxjs/operators';
import { IconComponent } from '../icons/icon-reusable';
import { exportIcon, ResetIcon, reassignIcon } from '../icons/icon';
import { DatePickerReusableComponent } from '../date-picker-reusable/date-picker-reusable';
import { ActionMenuComponent, ActionMenuItem } from '../action-menu/action-menu';
import { ButtonComponent } from '../button-reusable/button-reusable';
import { TooltipDirective } from '../tooltip/tooltip.directive';
import { SelectComponent, SelectOption } from '../select/select';

export interface FilterOption {
  value: any;
  label: string;
  subtext?: string;
}

export interface FilterConfig {
  key: string;
  label: string;
  type: 'select' | 'text' | 'number' | 'date';
  options?: FilterOption[];
  placeholder?: string;
  searchable?: boolean;
  /**
   * Renders the select as a multi-select: the menu stays open, picked options are
   * ticked, and the control's value is an array rather than a single value —
   * which is what a consumer reading `filtersChange` has to expect.
   */
  multiple?: boolean;
  /**
   * Every filter gets its own clear (×) control while it holds a value, so it can
   * be cleared on its own without Reset all. Set `false` to opt a filter out.
   */
  clearable?: boolean;
  /**
   * The options are fetched for what is typed rather than filtered in the
   * browser, so the select can reach past the page of them it was given. The
   * consumer listens on `filterSearch` and hands back a new `options` list.
   */
  remoteSearch?: boolean;
  /** Greys the select out — for a filter that only makes sense once another one is set. */
  disabled?: boolean;
  /**
   * One select over several lists (Lender / Provider): a switch inside the menu picks the list
   * and only its options show. The value says which list it came from — `'<source key>:<value>'`
   * (see `filterSourceValue`) — and switching lists clears a pick from the other one.
   * Used in place of `options`.
   */
  sources?: FilterSource[];
}

export interface FilterSource {
  key: string;
  label: string;
  options: FilterOption[];
}

/** Splits a sourced filter's value into its list and the option's own value. */
export function filterSourceValue(value: unknown): { source: string; value: string } | null {
  const text = String(value ?? '');
  const at = text.indexOf(':');
  return at > 0 ? { source: text.slice(0, at), value: text.slice(at + 1) } : null;
}

export interface TabConfig {
  label: string;
  value: string;
  count?: number;
}

export interface TableFilterSectionConfig {
  showSearch?: boolean;
  searchPlaceholder?: string;
  showDateRange?: boolean;
  /**
   * Whether the date range picker allows manual typing into editable inputs (dd-mm-yyyy).
   * Defaults to `true` (editable date range picker).
   * Set to `false` (or disableManualDateInput: true / nonEditableDateRange: true)
   * to use the non-editable date picker trigger.
   */
  manualDateInput?: boolean;
  allowManualDateInput?: boolean;
  manualDate?: boolean;
  allowManualDate?: boolean;
  disableManualDateInput?: boolean;
  nonEditableDateRange?: boolean;
  editableDateRange?: boolean;
  dateFormat?: string;
  filters?: FilterConfig[];
  showReset?: boolean;
  showExport?: boolean;
  showMarkAll?: boolean;
  showTabs?: boolean;
  tabs?: TabConfig[];
  showFilterChips?: boolean;
  initialFilters?: Record<string, any>;
  showReassign?: boolean;
  reassignTooltip?: string;
}

@Component({
  selector: 'app-table-filter-section',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IconComponent, ButtonComponent, TooltipDirective, SelectComponent, ActionMenuComponent, DatePickerReusableComponent],
  templateUrl: './table-filter-section-reusable.html',
  styleUrl: './table-filter-section-reusable.css'
})
export class TableFilterSectionComponent implements OnInit, OnChanges, OnDestroy {
  private readonly formBuilder = inject(FormBuilder);

  @Input() config: TableFilterSectionConfig = {};
  @Input() activeTab = '';
  @Input() exportDisabled = false;
  @Input() exportPermission = true;
  /**
   * Formats the Export button offers. Left empty, the button exports straight
   * away as it always has; given options, it opens a menu and the chosen one is
   * emitted on `exportOptionSelected` instead.
   */
  @Input() exportOptions: ActionMenuItem[] = [];
  @Input() exportLoading = false;
  @Input() reassignDisabled = false;
  @Input() containerClass = 'mb-5 rounded-md';
  /** Kept so existing bindings compile; the date range is the shared picker, always typeable. */
  @Input() manualDateInput?: boolean;
  @Input() allowManualDateInput?: boolean;
  @Input() disableManualDateInput?: boolean;
  @Input() nonEditableDateRange?: boolean;
  @Input() editableDateRange?: boolean;

  @Output() filtersChange = new EventEmitter<Record<string, any>>();
  @Output() search = new EventEmitter<string>();
  /** A `remoteSearch` filter's own dropdown search, so its options can be refetched. */
  @Output() filterSearch = new EventEmitter<{ key: string; query: string }>();
  @Output() reset = new EventEmitter<void>();
  @Output() export = new EventEmitter<Record<string, any>>();
  @Output() exportOptionSelected = new EventEmitter<ActionMenuItem>();
  @Output() tabChange = new EventEmitter<string>();
  @Output() markAllRead = new EventEmitter<void>();
  @Output() reassign = new EventEmitter<void>();

  readonly filterForm: FormGroup = this.formBuilder.group({
    search: this.formBuilder.control(''),
    startDate: this.formBuilder.control(''),
    endDate: this.formBuilder.control('')
  });

  activeFilterChips: Array<{ key: string; label: string }> = [];
  readonly exportIcon = exportIcon;
  readonly resetIcon = ResetIcon;
  readonly reassignIcon = reassignIcon;
  isExportMenuOpen = false;

  private readonly baseControlKeys = ['search', 'startDate', 'endDate'];
  private readonly subscriptions = new Subscription();
  private applyingState = false;
  private lastEmittedSearch = '';
  private lastEmittedFiltersPayload = this.serializeFiltersPayload(
    this.buildFilterPayload(this.filterForm.getRawValue())
  );

  ngOnInit(): void {
    this.syncFormWithConfig();
    this.setupListeners();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config']) {
      this.syncFormWithConfig();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  onReset(): void {
    const nextValue: Record<string, any> = {
      search: '',
      startDate: '',
      endDate: ''
    };

    for (const filter of this.config.filters ?? []) {
      nextValue[filter.key] = this.emptyValueFor(filter);
    }

    this.applyFormValue(nextValue);
    this.reset.emit();
  }

  onExport(event?: Event): void {
    if (this.exportDisabled || this.exportLoading) {
      return;
    }

    if (this.exportOptions.length) {
      // Kept off the document, so the menu's own outside-click listener does not
      // close it in the same click that opened it.
      event?.stopPropagation();
      this.isExportMenuOpen = !this.isExportMenuOpen;
      return;
    }

    this.export.emit(this.filterForm.getRawValue());
  }

  onExportOptionSelected(item: ActionMenuItem): void {
    this.isExportMenuOpen = false;
    this.exportOptionSelected.emit(item);
  }

  closeExportMenu(): void {
    this.isExportMenuOpen = false;
  }

  onReassign(): void {
    this.reassign.emit();
  }

  trackFilter(_index: number, filter: FilterConfig): string {
    return filter.key;
  }

  getSelectOptions(filter: FilterConfig): SelectOption[] {
    if (filter.sources?.length) {
      const source = filter.sources.find((item) => item.key === this.activeSourceOf(filter));
      return (source?.options ?? []).map((option) => ({ ...option, value: `${source!.key}:${option.value}` }));
    }
    return (filter.options || []).filter((option) => !this.isFilterLabelPlaceholder(filter, option));
  }

  /** Each sourced filter's open list, by filter key, until a value decides it. */
  private readonly pickedSources: Record<string, string> = {};

  /** The list a sourced filter shows: the one its value came from, else the last picked, else the first. */
  activeSourceOf(filter: FilterConfig): string {
    const sources = filter.sources ?? [];
    const fromValue = filterSourceValue(this.filterForm.get(filter.key)?.value)?.source;
    if (fromValue && sources.some((item) => item.key === fromValue)) return fromValue;
    return this.pickedSources[filter.key] ?? sources[0]?.key ?? '';
  }

  /** Kept per sources list, so the switch's buttons aren't rebuilt (mid-click) on every check. */
  private readonly sourceTabs = new WeakMap<FilterSource[], { value: string; label: string }[]>();
  private static readonly NO_SOURCES: { value: string; label: string }[] = [];

  getFilterSources(filter: FilterConfig): { value: string; label: string }[] {
    const sources = filter.sources;
    if (!sources?.length) return TableFilterSectionComponent.NO_SOURCES;
    let tabs = this.sourceTabs.get(sources);
    if (!tabs) {
      tabs = sources.map((item) => ({ value: item.key, label: item.label }));
      this.sourceTabs.set(sources, tabs);
    }
    return tabs;
  }

  /** Switching lists drops a pick from the other one — the filter narrows by one list at a time. */
  onSourceChange(filter: FilterConfig, source: string): void {
    this.pickedSources[filter.key] = source;
    const control = this.filterForm.get(filter.key);
    const current = filterSourceValue(control?.value);
    if (current && current.source !== source) {
      control?.setValue(this.emptyValueFor(filter));
    }
  }

  private isFilterLabelPlaceholder(filter: FilterConfig, option: FilterOption): boolean {
    const hasEmptyValue = option.value === '' || option.value === null || option.value === undefined;
    const optionLabel = option.label.trim().toLowerCase();
    const filterLabel = filter.label.trim().toLowerCase();

    return hasEmptyValue && optionLabel === filterLabel;
  }

  onTabChange(tabValue: string): void {
    this.activeTab = tabValue;
    this.tabChange.emit(tabValue);
  }

  /**
   * Clears one filter on its own — the × on a control and on an active-filter
   * chip both land here, so every page gets it without wiring anything up.
   * `search` and `dateRange` are handled alongside the configured filters.
   */
  removeFilter(key: string, event?: Event): void {
    event?.stopPropagation();

    if (key === 'dateRange') {
      this.filterForm.patchValue({ startDate: '', endDate: '' });
      return;
    }

    const filter = this.config.filters?.find((item) => item.key === key);
    this.filterForm.get(key)?.setValue(filter ? this.emptyValueFor(filter) : '');
  }

  /** Whether a filter's own clear control should show — only while it is narrowing anything. */
  canClearFilter(key: string): boolean {
    if (key === 'dateRange') {
      return (
        this.hasFilterValue(this.filterForm.get('startDate')?.value) ||
        this.hasFilterValue(this.filterForm.get('endDate')?.value)
      );
    }

    const filter = this.config.filters?.find((item) => item.key === key);
    if (filter?.clearable === false) {
      return false;
    }

    return this.hasFilterValue(this.filterForm.get(key)?.value);
  }

  getFilterChipsCount(): number {
    return this.activeFilterChips.length;
  }

  get resetDisabled(): boolean {
    const formValue = this.filterForm.getRawValue();

    if (String(formValue.search ?? '').trim() !== '') {
      return false;
    }

    if (String(formValue.startDate ?? '').trim() !== '' || String(formValue.endDate ?? '').trim() !== '') {
      return false;
    }

    for (const filter of this.config.filters ?? []) {
      if (this.hasFilterValue(formValue[filter.key])) {
        return false;
      }
    }

    return true;
  }

  /** What an unset control holds — a multi-select is empty as a list, not as text. */
  private emptyValueFor(filter: FilterConfig): any {
    return filter.multiple ? [] : '';
  }

  /** True while the control is narrowing anything, for either kind of select. */
  private hasFilterValue(value: any): boolean {
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return String(value ?? '').trim() !== '';
  }

  get resetIconClasses(): string {
    return 'text-inherit shrink-0';
  }

  get exportIconClasses(): string {
    return 'text-inherit shrink-0';
  }

  get reassignTooltip(): string {
    return this.config.reassignTooltip || '';
  }

  private syncFormWithConfig(): void {
    const currentValue = this.filterForm.getRawValue();
    const initialFilters = this.config.initialFilters ?? {};
    // A pick still inside the valueChanges debounce has not been emitted yet. A
    // config change landing in that window — a remote-search select reloading its
    // options as it closes, say — must not mark it as sent, or it is never applied.
    const emittedBaseline = this.lastEmittedFiltersPayload;
    const hasPendingChange =
      this.serializeFiltersPayload(this.buildFilterPayload(currentValue)) !== emittedBaseline;
    const allowedKeys = new Set<string>([...this.baseControlKeys, ...(this.config.filters ?? []).map((filter) => filter.key)]);

    for (const key of Object.keys(this.filterForm.controls)) {
      if (!allowedKeys.has(key)) {
        this.filterForm.removeControl(key);
      }
    }

    for (const filter of this.config.filters ?? []) {
      if (!this.filterForm.contains(filter.key)) {
        this.filterForm.addControl(filter.key, new FormControl(this.emptyValueFor(filter)));
      }
    }

    const initialValue: Record<string, any> = {
      search: this.resolveInitialFilterValue('search', currentValue, initialFilters),
      startDate: this.resolveInitialFilterValue('startDate', currentValue, initialFilters),
      endDate: this.resolveInitialFilterValue('endDate', currentValue, initialFilters)
    };

    for (const filter of this.config.filters ?? []) {
      initialValue[filter.key] = this.resolveInitialFilterValue(filter.key, currentValue, initialFilters, filter);
    }

    this.applyFormValue(initialValue);
    if (hasPendingChange) {
      this.lastEmittedFiltersPayload = emittedBaseline;
    }
  }

  private resolveInitialFilterValue(
    key: string,
    currentValue: Record<string, any>,
    initialFilters: Record<string, any>,
    filter?: FilterConfig
  ): any {
    // Prefer current value if it's already set (user may have already interacted with the filter)
    if (Object.prototype.hasOwnProperty.call(currentValue, key) && this.hasFilterValue(currentValue[key])) {
      return currentValue[key];
    }

    // Fall back to initial filters
    if (Object.prototype.hasOwnProperty.call(initialFilters, key)) {
      return initialFilters[key];
    }

    return filter ? this.emptyValueFor(filter) : '';
  }

  private setupListeners(): void {
    const searchControl = this.filterForm.get('search');

    if (searchControl) {
      this.subscriptions.add(
        searchControl.valueChanges.pipe(
          debounceTime(300),
          map((value) => String(value ?? ''))
        ).subscribe((value) => {
          if (this.applyingState) {
            return;
          }

          if (value === this.lastEmittedSearch) {
            this.updateFilterChips();
            return;
          }

          this.lastEmittedSearch = value;
          this.search.emit(value);
          this.updateFilterChips();
        })
      );
    }

    this.subscriptions.add(
      this.filterForm.valueChanges.pipe(
        debounceTime(150),
        map((value) => this.buildFilterPayload(value))
      ).subscribe((value) => {
        if (this.applyingState) {
          return;
        }

        const serializedValue = this.serializeFiltersPayload(value);
        if (serializedValue === this.lastEmittedFiltersPayload) {
          this.updateFilterChips();
          return;
        }

        this.lastEmittedFiltersPayload = serializedValue;
        this.filtersChange.emit(value);
        this.updateFilterChips();
      })
    );
  }

  private applyFormValue(value: Record<string, any>): void {
    this.applyingState = true;
    this.filterForm.patchValue(value, { emitEvent: false });
    const nextValue = this.filterForm.getRawValue();
    this.lastEmittedSearch = String(nextValue['search'] ?? '');
    this.lastEmittedFiltersPayload = this.serializeFiltersPayload(
      this.buildFilterPayload(nextValue)
    );
    this.updateFilterChips();
    this.applyingState = false;
  }

  private buildFilterPayload(value: Record<string, any>): Record<string, any> {
    const { search, ...rest } = value;
    return rest;
  }

  private serializeFiltersPayload(value: Record<string, any>): string {
    return JSON.stringify(value);
  }

  private updateFilterChips(): void {
    if (!this.config.showFilterChips) {
      this.activeFilterChips = [];
      return;
    }

    const formValue = this.filterForm.getRawValue();
    const chips: Array<{ key: string; label: string }> = [];

    if (formValue.search && String(formValue.search).trim() !== '') {
      chips.push({
        key: 'search',
        label: `Search: "${formValue.search}"`
      });
    }

    for (const filter of this.config.filters ?? []) {
      const value = formValue[filter.key];
      if (!this.hasFilterValue(value)) {
        continue;
      }

      // A multi-select is one chip listing everything it is holding, so clearing
      // it clears the filter rather than a single value inside it.
      const picked = Array.isArray(value) ? value : [value];
      const sourced = filter.sources?.length ? filterSourceValue(value) : null;
      if (sourced) {
        const source = filter.sources!.find((item) => item.key === sourced.source);
        const label = source?.options.find((option) => String(option.value) === sourced.value)?.label ?? sourced.value;
        chips.push({ key: filter.key, label: `${source?.label ?? filter.label}: ${label}` });
        continue;
      }

      const labels = picked.map(
        (item) => filter.options?.find((option) => option.value === item)?.label ?? item
      );

      chips.push({
        key: filter.key,
        label: `${filter.label}: ${labels.join(', ')}`
      });
    }

    if (formValue.startDate || formValue.endDate) {
      const start = formValue.startDate || 'Any';
      const end = formValue.endDate || 'Any';
      chips.push({
        key: 'dateRange',
        label: `Date: ${start} to ${end}`
      });
    }

    this.activeFilterChips = chips;
  }
}
